import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, parseNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileCheck2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const FUEL_TYPES = ["Benzina", "Diesel", "GPL", "Metano", "Elettrico", "AdBlue"];

export default function RequestEditDialog({ request, open, onClose, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (open) api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (request) {
      setFile(null);
      setForm({
        vehicle_id: request.vehicle_id || "",
        usage_type: request.usage_type || "km",
        usage_value: request.usage_value ?? "",
        fuel_type: request.fuel_type || "Diesel",
        amount: request.amount ?? "",
        actual_amount: request.actual_amount ?? "",
        liters: request.liters ?? "",
        notes: request.notes || "",
      });
    }
  }, [request]);

  const save = async () => {
    setSaving(true);
    try {
      let receiptPath;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receiptPath = data.path;
      }
      await api.put(`/requests/${request.id}`, {
        vehicle_id: form.vehicle_id,
        usage_type: form.usage_type,
        usage_value: parseNum(form.usage_value) || 0,
        fuel_type: form.fuel_type,
        amount: parseNum(form.amount) || 0,
        actual_amount: form.actual_amount === "" ? undefined : (parseNum(form.actual_amount) || 0),
        liters: form.liters === "" ? undefined : (parseNum(form.liters) || 0),
        receipt_path: receiptPath,
        notes: form.notes,
      });
      toast.success("Richiesta aggiornata");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="request-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifica richiesta</DialogTitle>
          <DialogDescription>{request?.employee_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Mezzo</Label>
            <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="edit-request-vehicle"><SelectValue placeholder="Seleziona un mezzo" /></SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo di indicazione</Label>
            <RadioGroup value={form.usage_type} onValueChange={(v) => setForm({ ...form, usage_type: v })} className="mt-2 flex gap-6">
              <div className="flex items-center gap-2"><RadioGroupItem value="km" id="er-km" data-testid="edit-request-km" /><Label htmlFor="er-km" className="font-normal">Km</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="ore" id="er-ore" data-testid="edit-request-ore" /><Label htmlFor="er-ore" className="font-normal">Ore</Label></div>
            </RadioGroup>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="er-usage">{form.usage_type === "km" ? "Km" : "Ore"}</Label>
              <Input id="er-usage" type="text" inputMode="decimal" data-testid="edit-request-usage" value={form.usage_value} onChange={(e) => setForm({ ...form, usage_value: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="er-amount">Importo aut. (€)</Label>
              <Input id="er-amount" type="text" inputMode="decimal" data-testid="edit-request-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="er-actual">Importo effettivo (€)</Label>
              <Input id="er-actual" type="text" inputMode="decimal" data-testid="edit-request-actual-amount" value={form.actual_amount} onChange={(e) => setForm({ ...form, actual_amount: e.target.value })} className="mt-1.5" placeholder="da scontrino" />
            </div>
            <div>
              <Label htmlFor="er-liters">Litri prelevati</Label>
              <Input id="er-liters" type="text" inputMode="decimal" data-testid="edit-request-liters" value={form.liters} onChange={(e) => setForm({ ...form, liters: e.target.value })} className="mt-1.5" placeholder="da scontrino" />
            </div>
          </div>
          <div>
            <Label>Tipo di carburante</Label>
            <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
              <SelectTrigger className="mt-1.5" data-testid="edit-request-fuel"><SelectValue /></SelectTrigger>
              <SelectContent>{FUEL_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Scontrino</Label>
            <input ref={inputRef} type="file" accept="image/*,.pdf" capture="environment" className="hidden" data-testid="edit-request-receipt-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} data-testid="edit-request-pick-receipt" className="rounded-md">
                {file ? <FileCheck2 className="mr-2 h-4 w-4 text-emerald-600" /> : <Upload className="mr-2 h-4 w-4" />}
                {file ? file.name : (request?.receipt_path ? "Sostituisci scontrino" : "Carica scontrino")}
              </Button>
              {request?.receipt_path && !file && <span className="text-xs text-emerald-600" data-testid="edit-request-receipt-present">Scontrino già presente</span>}
            </div>
          </div>
          <div>
            <Label htmlFor="er-notes">Note</Label>
            <Textarea id="er-notes" data-testid="edit-request-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="save-request-edit-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
